import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MetaTitle from '@/Components/UI/MetaTitle'
import PseudoHeader from '@/Components/PseudoHeader/PseudoHeader'
import InputView from '@/Views/FormSteps/InputView'
import { MapIcon, Draw, RepeatCalender } from '@/Components/UI/Icons'

interface RouteOption {
  icon: React.ReactNode
  label: string
  description: string
  tags: string[]
  path: string
}

const ROUTE_OPTIONS: RouteOption[] = [
  {
    icon: <Draw />,
    label: 'Fill out an event brief',
    description: 'Tell us about the event and our team will begin sourcing for you',
    tags: ['Conferences', 'Complex events', 'Multi-day events', '50+ attendees'],
    path: 'location',
  },
  {
    icon: <MapIcon />,
    label: 'Explore venues on a Map',
    description: 'Recommended for browsing venues and planning simple events',
    tags: ['Private dining', 'Meetings', 'Socials', '1-49 attendees'],
    path: 'map-view',
  },
  {
    icon: <RepeatCalender />,
    label: 'Repeat an event or request a specific venue',
    description: 'Choose a previous event or venue you like and update the requirements',
    tags: ['Repeat bookings', 'Specific venue selection', 'Quick planning'],
    path: 'repeat-event',
  },
]

const ChooseRouteInput: InputView = () => {
  const navigate = useNavigate()
  const [selectedPath, setSelectedPath] = useState(ROUTE_OPTIONS[0].path)

  return (
    <main className='choose-route-page flex flex-col justify-center'>
      <MetaTitle title='Submit your event enquiry - HeadBox' />
      <PseudoHeader />
      <div className='mx-auto w-full max-w-[792px] pt-15'>
        <div className='flex flex-col mb-9'>
          <h2 className='m-0 text-center text-2xl font-semibold leading-8 text-black'>
            How would you like to submit your event enquiry?
          </h2>
        </div>
        <div className='flex flex-col gap-[30px] text-left'>
          {ROUTE_OPTIONS.map(({ icon, label, description, tags, path }) => {
            const isSelected = selectedPath === path
            return (
              <div
                key={path}
                role='button'
                tabIndex={0}
                onClick={() => setSelectedPath(path)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedPath(path)}
                className='flex flex-col cursor-pointer gap-4 rounded-2xl 
                border border-[#bfbfbf] bg-white p-4 sm:flex-row sm:items-center'>
                <div className='flex min-w-0 flex-1 flex-col gap-4'>
                  <div className='flex items-center gap-2'>
                    <span
                      className={`inline-flex size-5 flex-shrink-0 items-center justify-center rounded-full border-1 ${
                        isSelected
                          ? 'border-black after:size-[10px] after:rounded-full after:bg-black' +
                            ' after:content-[""]'
                          : 'border-black'
                      }`}
                      aria-hidden='true'
                    />
                    <h2 className='m-0 text-2xl font-semibold leading-8 text-black sm:whitespace-nowrap'>
                      <span className='flex items-center gap-2'>
                        <span className='flex-shrink-0'>{icon}</span>
                        <span>{label}</span>
                      </span>
                    </h2>
                  </div>
                  <p className='m-0 text-base font-semibold leading-6 text-[#8c8c8c]'>
                    {description}
                  </p>
                  <div className='flex flex-col gap-[10px] sm:flex-row sm:flex-wrap sm:items-center'>
                    <span className='text-sm font-normal text-[#8c8c8c]'>Best for:</span>
                    <ul
                      className='m-0 grid grid-cols-2 text-center sm:text-start 
                    list-none gap-[10px] p-0 sm:flex sm:flex-row sm:flex-wrap'>
                      {tags.map((tag) => (
                        <li
                          key={tag}
                          className='rounded-lg bg-[#f0f0f0] px-2 py-1 text-xs font-medium text-black'>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <button
                  className={`hb-button w-full sm:w-auto sm:flex-shrink-0 sm:self-center ${
                    isSelected ? 'primary-button' : 'secondary-button'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/${path}`)
                  }}
                  type='button'>
                  Let&apos;s Go!
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </main>
  )
}

export default ChooseRouteInput
